import type { RevitFamily } from '../../api/revit/types';
import {
  countUndoPlanEntries,
  countUndoPlanFamilies,
} from '../../lib/parameterUndo/undoPlanCounts';
import type {
  ConnectorParameterRestoreEntry,
  ConnectorParameterUndoEntry,
  ConnectorParameterUndoPlan,
} from '../../types';
import type { BoltConfigurationResult } from '../boltConfigurationService';
import {
  buildBoltFamilyUndoEntries,
  extractBoltParameterNames,
  type BoltConfigurationInput,
} from './planner';

export function createBoltUndoPlan(input: {
  result: BoltConfigurationResult;
  configInput: BoltConfigurationInput;
  familiesBeforeApply: RevitFamily[];
  projectKey: string;
}): ConnectorParameterUndoPlan | null {
  const parameterNames = extractBoltParameterNames(input.configInput);
  if (!input.result.success || parameterNames.length === 0) {
    return null;
  }

  let projectParameterNames: string[] = [];
  let entries: ConnectorParameterUndoEntry[] = [];
  let familyParameterRestoreEntries: ConnectorParameterRestoreEntry[] = [];
  const targetObjectType = input.result.targetObjectType;

  if (input.result.details?.projectParameterNames) {
    projectParameterNames = input.result.details.projectParameterNames;
  } else if (input.result.action === 'added_project_parameters') {
    projectParameterNames = parameterNames;
  }

  if (input.result.action === 'added_to_existing') {
    entries = buildBoltFamilyUndoEntries(
      input.familiesBeforeApply,
      targetObjectType,
      input.configInput.objectTypeParameterName,
      parameterNames
    );
  }

  if (
    input.result.details?.loadedFamilyId &&
    input.result.details.loadedFamilyName
  ) {
    const loadedFamilyId = input.result.details.loadedFamilyId;
    const loadedFamilyName = input.result.details.loadedFamilyName;
    const addedFamilyParameterNames =
      input.result.details.addedFamilyParameterNames ?? [];
    const reboundFamilyParameters =
      input.result.details.reboundFamilyParameters ?? [];
    const removedDefaultParameters =
      input.result.details.removedDefaultParameters ?? [];

    entries = [
      ...addedFamilyParameterNames.map((parameterName) => ({
        familyId: loadedFamilyId,
        familyName: loadedFamilyName,
        parameterName,
        existedBefore: false,
      })),
      ...reboundFamilyParameters.map((parameter) => ({
        familyId: loadedFamilyId,
        familyName: loadedFamilyName,
        parameterName: parameter.parameterName,
        existedBefore: true,
        previousBindingKind: parameter.previousBindingKind,
      })),
    ];
    familyParameterRestoreEntries = removedDefaultParameters.map((parameter) => ({
      familyId: loadedFamilyId,
      familyName: loadedFamilyName,
      parameterName: parameter.parameterName,
      bindingKind: parameter.bindingKind,
      targetObjectType,
    }));
  }

  if (
    projectParameterNames.length === 0 &&
    entries.length === 0 &&
    familyParameterRestoreEntries.length === 0
  ) {
    return null;
  }

  const material = { entries, familyParameterRestoreEntries };

  return {
    id: `bolt-undo-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    createdAt: new Date().toISOString(),
    projectKey: input.projectKey,
    projectParameterNames,
    familyCount: countUndoPlanFamilies(material),
    entryCount: countUndoPlanEntries(material),
    status: 'available',
    entries,
    ...(familyParameterRestoreEntries.length > 0 ? { familyParameterRestoreEntries } : {}),
  };
}
